import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import logo from "../assets/MaAz_Logo.png";
import { getDeviceContext } from "../services/deviceContext";

function SSOEntry() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const startedRef = useRef(false);

  const [message, setMessage] = useState("Signing you in to MaAz LMS…");
  const [error, setError] = useState("");

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    async function exchangeCode() {
      const code = searchParams.get("code");

      if (!code) {
        setError("SSO code is missing.");
        setMessage("");
        return;
      }

      try {
        // Use the exact same physical-machine identity that MaAz Platform
        // used during login. Electron is the production source of truth.
        const { device_id, device_name } = await getDeviceContext();

        const response = await api.post(
          "/sso/exchange",
          {
            code,
            product: "LMS",
            device_id,
            device_name,
          },
          {
            // An existing LMS token must never be required to exchange
            // a one-time SSO code.
            headers: {
              Authorization: undefined,
            },
          }
        );

        const payload = response.data?.data || response.data;
        const token = payload?.token;
        const user = payload?.user;

        if (!token || !user) {
          throw new Error("Invalid SSO response from MaAz Backend.");
        }

        if (user.role !== "student") {
          throw new Error("Only student accounts can open MaAz LMS.");
        }

        // Keep the exact localStorage keys already used by your LMS.
        localStorage.setItem("token", token);
        localStorage.setItem("student", JSON.stringify(user));

        // Remove the one-time code from browser history immediately.
        window.history.replaceState({}, document.title, "/dashboard");

        navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("SSO exchange failed:", err);

        const backendMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;

        setError(
          backendMessage ||
            "Unable to open MaAz LMS using your Platform session."
        );
        setMessage("");
      }
    }

    exchangeCode();
  }, [navigate, searchParams]);

  const returnToPlatform = () => {
    const platformUrl =
      process.env.REACT_APP_MAAZ_PLATFORM_URL || "http://localhost:5174";

    window.location.replace(platformUrl);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img src={logo} alt="MaAz Logo" style={styles.logoImg} />

        {!error ? (
          <>
            <div style={styles.spinner} />
            <h2 style={styles.heading}>{message}</h2>
            <p style={styles.text}>
              Please wait. Your MaAz Platform session is being verified.
            </p>
          </>
        ) : (
          <>
            <h2 style={styles.errorHeading}>Unable to open MaAz LMS</h2>
            <p style={styles.errorText}>{error}</p>

            <button style={styles.button} onClick={returnToPlatform}>
              Back to MaAz Platform
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "#f4f6f8",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    boxSizing: "border-box",
  },
  card: {
    width: "430px",
    maxWidth: "100%",
    background: "#fff",
    padding: "38px",
    borderRadius: "14px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
    boxSizing: "border-box",
  },
  logoImg: {
    width: "320px",
    maxWidth: "100%",
    marginBottom: "20px",
  },
  spinner: {
    width: "42px",
    height: "42px",
    border: "4px solid #eceff2",
    borderTopColor: "#d71920",
    borderRadius: "50%",
    margin: "8px auto 22px",
    animation: "spin 0.8s linear infinite",
  },
  heading: {
    margin: "0 0 10px",
    color: "#1f2937",
  },
  text: {
    margin: 0,
    color: "#6b7280",
    lineHeight: 1.6,
  },
  errorHeading: {
    margin: "0 0 12px",
    color: "#b91c1c",
  },
  errorText: {
    color: "#6b7280",
    lineHeight: 1.6,
    marginBottom: "22px",
  },
  button: {
    width: "100%",
    padding: "12px",
    background: "#2c3e50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
  },
};

export default SSOEntry;
